// features/workstations/components/domain-card.js
// ═══════════════════════════════════════════════════════════
// DOMAIN CARD — command-center card on the Workstation Home
//
// Rich card showing:
//   - Domain classification + status pill (In session / Idle Nh / Never opened)
//   - Workstation name (Cormorant Garamond hero)
//   - Today's goal (italic gold, editable later)
//   - Last action (auto from workstation_events)
//   - Tab badges (content counts)
//   - Resume → CTA
//
// Click → enters the 9-tab shell for that workstation.
// ═══════════════════════════════════════════════════════════

// Accent color per domain (locked palette mapping)
const DOMAIN_ACCENTS = {
  trading:  'gold',   // Trading domain = gold (identity pillar)
  deal:     'steel',  // Real estate / Deal = steel (data)
  survival: 'sage',   // Survival = sage (action/outdoor)
  writing:  'purple', // Writing = purple (depth)
  research: 'teal',   // Research = teal (connection)
};

export default function DomainCard({ workstation, onOpen }) {
  const ws = workstation;
  const accent = DOMAIN_ACCENTS[ws.domain_key] || 'gold';
  const status = ws.activity?.status || { key: 'never', label: 'Never opened' };
  const lastAction = ws.activity?.last_event;
  const todaysGoal = ws.config?.identity?.todays_goal || null;

  return (
    <button
      type="button"
      className="ws-card"
      onClick={() => onOpen?.(ws)}
    >
      <div className={`ws-card-top-accent ${accent}`} />
      <div className="ws-card-body">
        <div className="ws-card-header">
          <div className="ws-card-domain">{formatDomain(ws.domain_key)}</div>
          <div className={`ws-card-status-pill ${status.key}`}>{status.label}</div>
        </div>

        <div className="ws-card-name">{ws.name}</div>

        {todaysGoal ? (
          <div className="ws-card-field">
            <div className="ws-card-field-label">Today&apos;s goal</div>
            <div className="ws-card-field-value goal">&ldquo;{todaysGoal}&rdquo;</div>
          </div>
        ) : (
          <div className="ws-card-field">
            <div className="ws-card-field-label">Today&apos;s goal</div>
            <div className="ws-card-field-value" style={{ fontStyle: 'italic', opacity: 0.6 }}>Not set</div>
          </div>
        )}

        <div className="ws-card-field">
          <div className="ws-card-field-label">Last action</div>
          <div className="ws-card-field-value">
            {lastAction ? (
              <>
                {lastAction.summary || formatEventType(lastAction.type)}
                {' '}<em>· {relativeTime(lastAction.at)}</em>
              </>
            ) : (
              ws.description || 'No activity yet'
            )}
          </div>
        </div>

        <div className="ws-card-badges">
          {ws.description && ws.description.length < 80 && !lastAction && (
            <span>{ws.description}</span>
          )}
        </div>

        <div className="ws-card-footer">
          <div className="ws-card-meta">
            {ws.workstation_configured ? 'Configured' : 'Needs orientation'}
          </div>
          <div className="ws-card-cta">
            {lastAction ? 'Resume →' : 'Open →'}
          </div>
        </div>
      </div>
    </button>
  );
}

// ── Helpers ──────────────────────────────────────────────
function formatDomain(key) {
  const labels = {
    trading:  'Trading',
    deal:     'Real Estate',
    survival: 'Survival',
    writing:  'Writing',
    research: 'Research',
  };
  return labels[key] || (key || '').toUpperCase();
}

function formatEventType(type) {
  const labels = {
    chat_message:   'Chat message',
    upload:         'Upload',
    finding:        'Finding captured',
    work_save:      'Work saved',
    session_open:   'Session opened',
    session_close:  'Session closed',
  };
  return labels[type] || type;
}

function relativeTime(iso) {
  if (!iso) return '';
  const ageMs = Date.now() - new Date(iso).getTime();
  const ageMin = Math.floor(ageMs / 60000);
  if (ageMin < 1) return 'just now';
  if (ageMin < 60) return `${ageMin}m ago`;
  const ageHr = Math.floor(ageMin / 60);
  if (ageHr < 24) return `${ageHr}h ago`;
  const ageDay = Math.floor(ageHr / 24);
  return `${ageDay}d ago`;
}
